// frontend/src/modules/esign/pages/SignPage.tsx
//
// Public, host-agnostic signer experience at /sign/:token — no auth, no
// dashboard chrome. Loads the session (which server-side flips the
// recipient to 'viewed' on first load), renders a state screen for any
// non-active session state, and otherwise renders the PDF with this
// recipient's fields overlaid as inline inputs plus a consent + submit
// footer. CC recipients get a view-only banner and never see fields/submit.
import * as React from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, Ban, Clock, Download, FileText } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { LoadingState } from '../../../design-system/components/LoadingState';
import { usePrompt } from '../../../components/ui/confirm-dialog';
import { PdfPageCanvas } from '../components/PdfPageCanvas';
import { SignatureModal, type SignatureResult } from '../components/SignatureModal';
import { loadPdf, type PDFDocumentProxy } from '../pdf';
import { esignPublicApi } from '../api';
import type { EsignField, EsignSession } from '../types';

const CONSENT_COPY =
  'I agree to be legally bound by this electronic signature and consent to do business electronically.';

function StateScreen({
  icon,
  title,
  description,
  action,
}: {
  icon: React.ReactNode;
  title: string;
  description?: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <div className="max-w-md w-full text-center rounded-3xl bg-white/80 dark:bg-white/[0.05] backdrop-blur-xl ring-1 ring-ink/8 dark:ring-white/10 p-10 shadow-card">
        <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-ink/5 dark:bg-white/10">
          {icon}
        </div>
        <h1 className="font-light text-2xl text-ink dark:text-white tracking-[-0.02em]">{title}</h1>
        {description && (
          <p className="text-[14.5px] text-ink/60 dark:text-white/60 mt-3 leading-relaxed">{description}</p>
        )}
        {action && <div className="mt-6">{action}</div>}
      </div>
    </div>
  );
}

export function SignPage() {
  const { token } = useParams<{ token: string }>();
  const prompt = usePrompt();

  const [session, setSession] = React.useState<EsignSession | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [invalid, setInvalid] = React.useState(false);
  /** Set when the session fetch failed for a reason that says nothing about the token. */
  const [transientError, setTransientError] = React.useState<'rate_limited' | 'unavailable' | null>(null);
  const [pdf, setPdf] = React.useState<PDFDocumentProxy | null>(null);

  const [values, setValues] = React.useState<Record<string, string>>({});
  const [signature, setSignature] = React.useState<SignatureResult | null>(null);
  const [initialsDataUrl, setInitialsDataUrl] = React.useState<string | null>(null);
  const [consented, setConsented] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [signedOk, setSignedOk] = React.useState(false);

  const [sigModalOpen, setSigModalOpen] = React.useState(false);
  const [initialsModalOpen, setInitialsModalOpen] = React.useState(false);

  const [containerWidth, setContainerWidth] = React.useState(720);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const load = React.useCallback(() => {
    if (!token) return;
    setLoading(true);
    setInvalid(false);
    setTransientError(null);
    esignPublicApi.getSession(token)
      .then((s) => setSession(s))
      .catch((err: unknown) => {
        // Only a verdict FROM the server about this link means the link is bad.
        // A rate limit, a 5xx or a dropped connection says nothing about the
        // token — and telling that signer "this link is not valid" sends them
        // to email the sender for a replacement that would fail the same way.
        const status = (err as { status?: number } | null)?.status;
        if (status !== undefined && status < 500 && status !== 429) setInvalid(true);
        else setTransientError(status === 429 ? 'rate_limited' : 'unavailable');
      })
      .finally(() => setLoading(false));
  }, [token]);

  React.useEffect(() => { load(); }, [load]);

  React.useEffect(() => {
    function measure() {
      const w = containerRef.current?.clientWidth ?? 720;
      setContainerWidth(Math.min(w, 720));
    }
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const isActive = session?.me.state === 'ok' && session.me.role === 'signer' && session.me.can_sign;

  React.useEffect(() => {
    if (!token || !isActive) return;
    esignPublicApi.getSessionPdf(token).then(loadPdf).then(setPdf).catch(() => { /* PDF failed to load — page still usable for the state banner */ });
  }, [token, isActive]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingState />
      </div>
    );
  }

  if (transientError) {
    return (
      <StateScreen
        icon={<XCircle className="h-5 w-5 text-ink/50 dark:text-white/50" />}
        title="We couldn't load this document."
        description={transientError === 'rate_limited'
          ? 'Too many attempts from your connection. Wait a moment, then try again — your link is still fine.'
          : 'Something went wrong on our side. Your link is still fine — please try again in a moment.'}
        action={<Button type="button" variant="outline" size="sm" onClick={load}>Try again</Button>}
      />
    );
  }

  if (invalid || !session) {
    return (
      <StateScreen
        icon={<XCircle className="h-5 w-5 text-ink/50 dark:text-white/50" />}
        title="This link is not valid."
        description="This sign link may have expired or already been used. Contact the sender if you believe this is a mistake."
      />
    );
  }

  const { document: doc, me } = session;

  if (signedOk) {
    return (
      <StateScreen
        icon={<CheckCircle2 className="h-5 w-5 text-emerald-600" />}
        title="You're all signed."
        description={`You've signed ${doc.title}. You'll receive the final document by email once everyone has signed.`}
      />
    );
  }

  if (me.state === 'not_your_turn') {
    return (
      <StateScreen
        icon={<Clock className="h-5 w-5 text-ink/50 dark:text-white/50" />}
        title="It's not your turn yet"
        description="It's not your turn yet — you'll get an email when it's your turn to sign."
      />
    );
  }

  if (me.state === 'already_signed') {
    return (
      <StateScreen
        icon={<CheckCircle2 className="h-5 w-5 text-emerald-600" />}
        title="You've already signed"
        description={`You already signed ${doc.title}. Thank you.`}
      />
    );
  }

  if (me.state === 'expired') {
    return (
      <StateScreen
        icon={<Clock className="h-5 w-5 text-ink/50 dark:text-white/50" />}
        title="This signing link has expired."
        description="Ask the sender to extend it — they can do this from their dashboard."
      />
    );
  }

  if (me.state === 'declined') {
    return (
      <StateScreen
        icon={<XCircle className="h-5 w-5 text-rose-600" />}
        title="This document was declined"
        description="This document was declined and is no longer available for signature."
      />
    );
  }

  if (me.state === 'voided') {
    return (
      <StateScreen
        icon={<Ban className="h-5 w-5 text-ink/50 dark:text-white/50" />}
        title="This document was voided"
        description="The sender has voided this document. No further action is needed."
      />
    );
  }

  if (me.state === 'completed') {
    return (
      <StateScreen
        icon={<CheckCircle2 className="h-5 w-5 text-emerald-600" />}
        title="Document complete"
        description={`${doc.title} has been fully executed by all parties.`}
        action={
          <Button
            className="gap-1.5"
            onClick={() => {
              if (!token) return;
              esignPublicApi.getSessionPdf(token)
                .then((buf) => {
                  const blob = new Blob([buf], { type: 'application/pdf' });
                  window.open(URL.createObjectURL(blob), '_blank', 'noopener,noreferrer');
                })
                .catch(() => toast.error('Failed to download the document'));
            }}
          >
            <Download className="h-3.5 w-3.5" />
            Download
          </Button>
        }
      />
    );
  }

  // ---- CC view-only ----
  if (me.role === 'cc') {
    return (
      <div className="min-h-screen">
        <header className="px-6 py-5 border-b border-ink/8 dark:border-white/10">
          <div className="max-w-[720px] mx-auto">
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-ink/50 dark:text-white/50">{session.tenant_name}</p>
            <h1 className="font-light text-xl text-ink dark:text-white mt-1">{doc.title}</h1>
          </div>
        </header>
        <div className="max-w-[720px] mx-auto px-6 py-6">
          <div className="rounded-2xl bg-ink/5 dark:bg-white/[0.05] ring-1 ring-ink/8 dark:ring-white/10 px-5 py-4 mb-6 flex items-center gap-3">
            <FileText className="h-4 w-4 text-ink/50 dark:text-white/50 shrink-0" />
            <p className="text-[13.5px] text-ink/70 dark:text-white/70">
              You're viewing this document — no action is needed from you.
            </p>
          </div>
          {pdf && (
            <div ref={containerRef} className="overflow-auto rounded-2xl bg-ink/5 dark:bg-black/20 p-4">
              {Array.from({ length: doc.page_count }, (_, i) => i + 1).map((pageNumber) => (
                <div key={pageNumber} className="mb-4">
                  <PdfPageCanvas pdf={pdf} pageNumber={pageNumber} width={containerWidth} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ---- Active signing flow ----
  const myFields = session.fields;

  function fieldValueOk(f: EsignField): boolean {
    if (f.type === 'signature') return !!signature;
    if (f.type === 'initials') return !!initialsDataUrl;
    if (!f.required) return true;
    if (f.type === 'checkbox') return true; // checkbox is a boolean toggle, always "filled"
    return (values[f.id] ?? '').trim() !== '';
  }

  const allRequiredFilled = myFields.every((f) => (f.required ? fieldValueOk(f) : true));
  const hasSignatureField = myFields.some((f) => f.type === 'signature');
  const signatureOk = !hasSignatureField || !!signature;
  const canSubmit = consented && allRequiredFilled && signatureOk;

  function goToNextField() {
    const next = myFields.find((f) => f.required && !fieldValueOk(f));
    if (!next) return;
    const el = document.querySelector(`[data-field-id="${next.id}"]`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (el instanceof HTMLElement && (next.type === 'text' || next.type === 'date')) el.focus();
  }

  async function handleSubmit() {
    if (!token || !canSubmit || !signature) return;
    setSubmitting(true);
    try {
      const fields = myFields
        .filter((f) => f.type !== 'signature' && f.type !== 'initials')
        .map((f) => {
          if (f.type === 'checkbox') return { id: f.id, value: values[f.id] ?? 'false' };
          const raw = values[f.id];
          const isBlank = raw === undefined || raw.trim() === '';
          // Unfilled optional fields are omitted entirely rather than sent as
          // '' — the backend's format validators (e.g. the date regex) would
          // otherwise reject an intentionally-empty optional field. Required
          // fields always have a value here (gated by canSubmit).
          if (isBlank && !f.required) return null;
          return { id: f.id, value: raw ?? '' };
        })
        .filter((f): f is { id: string; value: string } => f !== null);
      await esignPublicApi.sign(token, {
        fields,
        signature: { kind: signature.kind, dataUrl: signature.dataUrl },
        initialsDataUrl: initialsDataUrl ?? undefined,
      });
      setSignedOk(true);
    } catch (err: any) {
      toast.error('Failed to submit your signature', { description: err?.message ?? String(err) });
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDecline() {
    if (!token) return;
    const reason = await prompt({
      title: 'Decline this document',
      description: 'Optionally tell the sender why.',
      placeholder: 'Reason (optional)',
      confirmLabel: 'Decline',
    });
    if (reason === null) return;
    try {
      await esignPublicApi.decline(token, reason || undefined);
      setSession((s) => (s ? { ...s, me: { ...s.me, state: 'declined' } } : s));
    } catch (err: any) {
      toast.error('Failed to decline', { description: err?.message ?? String(err) });
    }
  }

  const fieldsByPage = new Map<number, EsignField[]>();
  for (const f of myFields) {
    const arr = fieldsByPage.get(f.page) ?? [];
    arr.push(f);
    fieldsByPage.set(f.page, arr);
  }

  return (
    <div className="min-h-screen pb-32">
      <header className="px-6 py-5 border-b border-ink/8 dark:border-white/10">
        <div className="max-w-[720px] mx-auto">
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-ink/50 dark:text-white/50">{session.tenant_name}</p>
          <h1 className="font-light text-xl text-ink dark:text-white mt-1">{doc.title}</h1>
          <p className="text-[13px] text-ink/55 dark:text-white/55 mt-1">Please review and sign, {me.name}.</p>
        </div>
      </header>

      <div className="max-w-[720px] mx-auto px-6 py-6">
        {pdf ? (
          <div ref={containerRef} className="overflow-auto rounded-2xl bg-ink/5 dark:bg-black/20 p-4 space-y-4">
            {Array.from({ length: doc.page_count }, (_, i) => i + 1).map((pageNumber) => (
              <PdfPageCanvas key={pageNumber} pdf={pdf} pageNumber={pageNumber} width={containerWidth} data-testid={`sign-page-${pageNumber}`}>
                {(fieldsByPage.get(pageNumber) ?? []).map((f) => (
                  <FieldOverlay
                    key={f.id}
                    field={f}
                    value={values[f.id]}
                    onValueChange={(v) => setValues((cur) => ({ ...cur, [f.id]: v }))}
                    signature={f.type === 'signature' ? signature : null}
                    initialsDataUrl={f.type === 'initials' ? initialsDataUrl : null}
                    onOpenSignature={() => (f.type === 'signature' ? setSigModalOpen(true) : setInitialsModalOpen(true))}
                  />
                ))}
              </PdfPageCanvas>
            ))}
          </div>
        ) : (
          <div ref={containerRef} className="space-y-3" data-testid="sign-fields-fallback">
            {/* PDF still loading (or failed) in jsdom/tests — render fields inline so the flow is still testable/usable. */}
            {myFields.map((f) => (
              <FieldOverlay
                key={f.id}
                field={f}
                value={values[f.id]}
                onValueChange={(v) => setValues((cur) => ({ ...cur, [f.id]: v }))}
                signature={f.type === 'signature' ? signature : null}
                initialsDataUrl={f.type === 'initials' ? initialsDataUrl : null}
                onOpenSignature={() => (f.type === 'signature' ? setSigModalOpen(true) : setInitialsModalOpen(true))}
                inline
              />
            ))}
          </div>
        )}
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-white/90 dark:bg-ink-900/90 backdrop-blur-xl border-t border-ink/10 dark:border-white/10 px-6 py-4">
        <div className="max-w-[720px] mx-auto space-y-3">
          <label className="flex items-start gap-2.5 text-[13px] text-ink/75 dark:text-white/75">
            <input
              type="checkbox"
              role="checkbox"
              checked={consented}
              onChange={(e) => setConsented(e.target.checked)}
              aria-label={CONSENT_COPY}
              className="mt-0.5"
            />
            <span>{CONSENT_COPY}</span>
          </label>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <Button type="button" variant="ghost" size="sm" onClick={goToNextField}>
                Next field
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={handleDecline} className="text-rose-600 hover:text-rose-700">
                Decline
              </Button>
            </div>
            <Button type="button" onClick={handleSubmit} disabled={!canSubmit || submitting}>
              Submit
            </Button>
          </div>
        </div>
      </div>

      <SignatureModal
        open={sigModalOpen}
        onOpenChange={setSigModalOpen}
        onCapture={(result: SignatureResult) => setSignature(result)}
        title="Add your signature"
      />
      <SignatureModal
        open={initialsModalOpen}
        onOpenChange={setInitialsModalOpen}
        onCapture={(result: SignatureResult) => setInitialsDataUrl(result.dataUrl)}
        title="Add your initials"
        compact
      />
    </div>
  );
}

interface FieldOverlayProps {
  field: EsignField;
  value: string | undefined;
  onValueChange: (v: string) => void;
  signature: SignatureResult | null;
  initialsDataUrl: string | null;
  onOpenSignature: () => void;
  inline?: boolean;
}

function FieldOverlay({ field, value, onValueChange, signature, initialsDataUrl, onOpenSignature, inline }: FieldOverlayProps) {
  const boxStyle: React.CSSProperties = inline
    ? { position: 'relative', marginBottom: 8 }
    : {
        position: 'absolute',
        left: `${field.x * 100}%`,
        top: `${field.y * 100}%`,
        width: `${field.w * 100}%`,
        height: `${field.h * 100}%`,
      };

  if (field.type === 'signature') {
    return (
      <div data-field-id={field.id} style={boxStyle}>
        <button
          type="button"
          onClick={onOpenSignature}
          className="w-full h-full rounded border-2 border-dashed border-coral/60 bg-coral/5 flex items-center justify-center text-[11px] font-medium text-coral overflow-hidden"
          aria-label="Signature field"
        >
          {signature ? <img src={signature.dataUrl} alt="Your signature" className="max-h-full max-w-full object-contain" /> : 'Click to sign'}
        </button>
      </div>
    );
  }

  if (field.type === 'initials') {
    return (
      <div data-field-id={field.id} style={boxStyle}>
        <button
          type="button"
          onClick={onOpenSignature}
          className="w-full h-full rounded border-2 border-dashed border-coral/60 bg-coral/5 flex items-center justify-center text-[11px] font-medium text-coral overflow-hidden"
          aria-label="Initials field"
        >
          {initialsDataUrl ? <img src={initialsDataUrl} alt="Your initials" className="max-h-full max-w-full object-contain" /> : 'Initial'}
        </button>
      </div>
    );
  }

  if (field.type === 'checkbox') {
    return (
      <div data-field-id={field.id} style={boxStyle} className="flex items-center justify-center">
        <input
          type="checkbox"
          aria-label="Checkbox field"
          checked={value === 'true'}
          onChange={(e) => onValueChange(e.target.checked ? 'true' : 'false')}
        />
      </div>
    );
  }

  if (field.type === 'date') {
    return (
      <div data-field-id={field.id} style={boxStyle}>
        <Input
          type="date"
          aria-label="Date field"
          value={value ?? ''}
          onChange={(e) => onValueChange(e.target.value)}
          className="h-full text-[12px]"
        />
      </div>
    );
  }

  return (
    <div data-field-id={field.id} style={boxStyle}>
      <Input
        type="text"
        aria-label="Text field"
        value={value ?? ''}
        onChange={(e) => onValueChange(e.target.value)}
        className="h-full text-[12px]"
      />
    </div>
  );
}
